import { useEffect, useRef } from 'react';
import { useSessionStorage } from 'usehooks-ts';

import { useNavParams } from 'hooks';

import { ADMIN_SESSION_STORAGE_KEYS } from '../types';

import type { Header, TabType } from '../types';

export type TabView = {
  name: string;
  parent: string;
  tabIndex: number;
};

/**
 * Persists the active view to session storage - on hard reload and at unmount -
 * and restores it once, on the initial load.
 *
 * Call this *after* `useTabNavigation`: both write a `page` param in the same
 * commit and the restore has to be the one that lands, which is decided by the
 * order the effects were declared in.
 */
const useLastLocation = (
  tabbedElement: TabType,
  tabIndex: number,
  headers: Header[]
) => {
  'use memo';

  const { setParams } = useNavParams();
  const [currentView, setCurrentView] = useSessionStorage<null | TabView>(
    ADMIN_SESSION_STORAGE_KEYS.LAST_LOCATION,
    null
  );
  const tabbedElementRef = useRef<null | TabView>(null); // to store the current tabbed element

  // keeping the ref in step with the active view, so the listener below reads
  // the latest one without having to re-subscribe on every navigation
  useEffect(() => {
    tabbedElementRef.current = {
      name: tabbedElement.name,
      parent: tabbedElement.parent,
      tabIndex: tabIndex
    };
  }, [tabbedElement, tabIndex]);

  // setting currentView on hard reload and at unmount
  useEffect(() => {
    const storeCurrentView = () => setCurrentView(tabbedElementRef.current);

    window.addEventListener('beforeunload', storeCurrentView);

    return () => {
      window.removeEventListener('beforeunload', storeCurrentView);
      storeCurrentView();
    };
  }, [setCurrentView]);

  // The stored view is restored once, on the initial load, so the values that
  // restore needs are captured on the first render rather than tracked as
  // dependencies: `headers` is rebuilt and `setParams` is recreated on every
  // render, so depending on them would re-run the restore continuously and keep
  // overwriting whatever the user navigated to since.
  const initialLoad = useRef({ currentView, headers, setParams });

  // set the page param on initial load
  useEffect(() => {
    const { currentView, headers, setParams } = initialLoad.current;

    if (currentView && headers?.[currentView.tabIndex]) {
      const tab = headers[currentView.tabIndex].tabs?.find(
        (tab) => tab.name === currentView.name
      );

      if (tab) {
        setParams({
          page: tab.name.toLowerCase()
        });
      }
    }
  }, []);
};

export default useLastLocation;
